'use client';

import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

interface CourseDetailsReturnFocusOptions {
  contextKey: string;
  courseId: string | null;
  detailsMounted: boolean;
  detailsLoading: boolean;
}

interface ReturnTarget {
  contextKey: string;
  courseId: string;
  target: HTMLElement;
  observedCourse: boolean;
}

/** Restore list-origin focus after URL closure and the actual details teardown. */
export function useCourseDetailsReturnFocus(options: CourseDetailsReturnFocusOptions) {
  const latest = useRef(options);
  const mounted = useRef(false);
  const pending = useRef<ReturnTarget | null>(null);

  useLayoutEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; pending.current = null; };
  }, []);

  useLayoutEffect(() => {
    latest.current = options;
    const entry = pending.current;
    if (!entry) return;
    if (entry.contextKey !== options.contextKey || (options.courseId !== null && options.courseId !== entry.courseId)) {
      pending.current = null;
    } else if (options.courseId === entry.courseId) {
      entry.observedCourse = true;
    }
  });

  const rememberReturnTarget = useCallback((courseId: string, target?: HTMLElement | null) => {
    if (!mounted.current) return;
    pending.current = target ? { contextKey: latest.current.contextKey, courseId, target, observedCourse: false } : null;
  }, []);

  useEffect(() => {
    const entry = pending.current;
    if (!entry?.observedCourse || options.courseId !== null || options.detailsMounted || options.detailsLoading) return;
    // The route removes its URL selection before its data effect unmounts the
    // dialog. Run after both commits and shared Modal's passive focus teardown.
    const frame = window.requestAnimationFrame(() => {
      const current = latest.current;
      if (!mounted.current || pending.current !== entry) return;
      pending.current = null;
      if (current.contextKey !== entry.contextKey || current.courseId !== null || current.detailsMounted || current.detailsLoading || !entry.target.isConnected) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"], [role="alertdialog"][aria-modal="true"]')) return;
      if (entry.target.closest('[inert], [hidden], details:not([open])')) return;
      entry.target.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [options.contextKey, options.courseId, options.detailsMounted, options.detailsLoading]);

  return { rememberReturnTarget };
}
